/**
 * The turn breakdown — ONE timeline of the conversation, in turn order.
 *
 * This used to be three sections showing the same conversation from three
 * angles: the case-record deltas (`turn_state`), the guided walk (`steps`)
 * and the fast Q&A (`hybrid`). A reviewer had to read the same turn three
 * times, in three places, to know what happened in it. They are now one list:
 * one card per turn, ordered by `turn_no`, carrying that turn's deltas and
 * whatever body it actually has.
 *
 * ── What a card says ────────────────────────────────────────────────────
 *
 *   GUIDED  — the turn has diagnostic steps. Body: the step cards, with the
 *             Worked / Ruled out / Can't tell marks they always had.
 *   FAST    — the turn has a hybrid row. Body: the Q&A, with the
 *             Helpful / Partly / Wrong marks it always had.
 *   both    — both badges, both bodies. Nothing is dropped.
 *   neither — no badge. Turns that only carry extracted deltas are common
 *             (the extractor summarises a turn the capture never walked), and
 *             badging them GUIDED or FAST would be a lie about the data.
 *
 * The two mark vocabularies stay separate on purpose. A step mark answers
 * "did this move work?"; a hybrid mark answers "was this answer any good?".
 * They are different questions, they persist through different endpoints
 * (PATCH …/steps vs PATCH …/hybrid), and merging the views must not merge
 * them. This file only places the existing controls — it owns neither.
 *
 * ── The one rule this file enforces ─────────────────────────────────────
 *
 * A ruled-out entry whose `why` is blank (including zero-width spaces and
 * other Unicode whitespace) renders as "not tested", NOT as ruled out.
 * A cause nobody tried is still in play — it has not been disproved. This
 * matches the invariant in CaseRecord.jsx and is load-bearing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, X, Crown, ChevronRight } from "lucide-react";
import { StepItems, isMarkable, groupStepsByTurn, stepHeadline, stepLabel } from "./TrailSteps";
import { HybridTurn } from "./HybridTrail";
// The rows wear the learnings list's classes (info-entry*, info-header-line),
// so the two lists on the overlay read as one family.
import "./styles/informational.css";
import "./styles/turn-breakdown.css";

/* ── helpers ───────────────────────────────────────────────────────── */

/** Strip ALL Unicode whitespace (including zero-width space U+200B). */
const isBlank = (s) => {
  if (s == null) return true;
  if (typeof s !== "string") return !String(s).trim();
  // \s covers ASCII whitespace; add common zero-width chars explicitly.
  return s.replace(/[\s​‌‍﻿]+/g, "").length === 0;
};

const asList = (v) =>
  (Array.isArray(v) ? v : []).filter((x) => typeof x === "string" && x.trim());

/** One line of display text: whitespace collapsed, ends trimmed. */
const oneLine = (s) => (typeof s === "string" ? s.replace(/\s+/g, " ").trim() : "");

/** Ruled-out entries with a cause. Split by `why`: blank → not tested. */
const ruledOutOf = (delta) =>
  Array.isArray(delta?.causes_ruled_out)
    ? delta.causes_ruled_out.filter((r) => r && typeof r === "object" && r.cause)
    : [];

/**
 * Does this turn's delta say anything the case record can show? The same test
 * TurnDeltas uses to decide between its sections and `null` — a row whose
 * delta fails it is a row with "no case record changes", and the Case Record
 * view does not render it.
 */
export function deltaHasContent(delta, prevProblem) {
  if (!delta) return false;
  const problemChanged =
    delta.problem_definition != null && delta.problem_definition !== prevProblem;
  return !!(
    asList(delta.symptoms_added).length ||
    asList(delta.facts_added).length ||
    asList(delta.causes_activated).length ||
    ruledOutOf(delta).length ||
    problemChanged ||
    delta.root_cause ||
    delta.resolution ||
    delta.note
  );
}

/**
 * Whether a row has anything of the mounting view's own kind. Case Record
 * (`showBodies={false}`) shows a turn only when its delta changed something;
 * Expert (`showDeltas={false}`) only when it has steps or a Q&A row.
 */
export function rowIsVisible(row, { showDeltas = true, showBodies = true } = {}) {
  if (showDeltas && deltaHasContent(row.delta, row.prevProblem)) return true;
  return !!(showBodies && (row.items.length > 0 || row.hybridTurn));
}

/**
 * What the engineer DID on each turn, from the stored turn envelopes
 * (`turns`: `{n, user, question: {text, options[]} | null, …}`).
 *
 * A reply that matches one of the options the PREVIOUS turn displayed is a
 * click — the same call transcriptAdapter makes to draw an answered-question
 * card — anything else is typed.
 *
 * A click is an ANSWER only when the card was a real question. The "Helpful
 * followups" card is a menu of questions the engineer can ASK next: the
 * server records it with `pending: false` (sockets_tse.py, _FOLLOWUPS_MARKER)
 * and its text starts with "Helpful followups". Picking one of those is
 * "asked". Envelopes without `pending` fall back to the option's shape: a
 * question ("…?", "Explain …", "How …") is asked, anything else answered.
 *
 * Returns Map<turnNo, {kind: "answered" | "asked", text}>.
 */
export function engineerActions(turns) {
  const out = new Map();
  if (!Array.isArray(turns)) return out;
  const ordered = turns
    .filter((t) => t && typeof t === "object" && Number.isFinite(Number(t.n)))
    .map((t) => ({ env: t, n: Number(t.n) }))
    .sort((a, b) => a.n - b.n);
  let prevQ = null;
  for (const { env, n } of ordered) {
    const said = oneLine(env.user);
    if (said) {
      const lower = said.toLowerCase();
      const clicked = prevQ
        ? prevQ.options.find((o) => oneLine(o).toLowerCase() === lower)
        : undefined;
      if (clicked == null) {
        out.set(n, { kind: "asked", text: said });
      } else {
        const text = oneLine(clicked);
        out.set(n, { kind: isFollowupCard(prevQ) || (prevQ.pending == null && isQuestionShaped(text))
          ? "asked"
          : "answered", text });
      }
    }
    const q = env.question;
    prevQ = q && typeof q === "object" && Array.isArray(q.options)
      ? { ...q, options: q.options.filter((o) => typeof o === "string") }
      : null;
  }
  return out;
}

const FOLLOWUPS_MARKER = /^\s*helpful follow-?ups/i;
/** The followups card: suggestions to ask, not a question to answer. */
const isFollowupCard = (q) =>
  !!q && (q.pending === false || FOLLOWUPS_MARKER.test(typeof q.text === "string" ? q.text : ""));
const QUESTION_LEAD =
  /^(what|why|how|when|where|which|who|is|are|was|were|does|do|did|can|could|should|would|will|explain|show|compare|describe|tell|list|walk)\b/i;
const isQuestionShaped = (t) => /\?\s*$/.test(t) || QUESTION_LEAD.test(t);

/**
 * The row title: the single most important thing that happened on the turn.
 *
 * First that applies:
 *   0. the problem's FIRST statement (no earlier problem) → "Problem: <problem>"
 *   1. resolution → "Resolved: <resolution>"; else root cause → "Root cause: <cause>"
 *   2. a cause ruled out WITH a reason → "Ruled out: <cause>"
 *      (blank `why` is "not tested", never a ruling-out — see the file header)
 *   3. cause activated → "New cause: <cause>"
 *   4. fact added → "Fact: <fact>"
 *   5. symptom added → "Symptom: <symptom>"
 *   6. problem refined → "Problem: <problem>"
 *   7. what the engineer did: `Answered: "<option>"` (a click) or
 *      `Asked: "<text>"` (typed) — from the turn envelopes, else the turn's
 *      Q&A question, else a digression step's typed query
 *   8. a guided turn with none of that: its last step, by role —
 *      "Check: <headline>", "Fix: <headline>" …
 *   9. "Turn N"
 *
 * Every OTHER delta item on the turn (each cause, fact, symptom, the note, a
 * root cause behind a resolution, a not-tested cause) is counted into `more`
 * and listed in `others` for the "+N more" tooltip.
 *
 * Returns { prefix, text, more, others } — prefix is "" for "Turn N".
 */
export function titleForTurn({ turnNo, delta, prevProblem, action, hybridTurn, items }) {
  const candidates = []; // [prefix, text] in priority order
  // The FIRST statement of the problem is the turn's headline: turn 1 is where
  // the engineer said what is wrong, and burying it under "+N more" behind a
  // symptom hid the Problem row. A later refinement keeps its old rank.
  const problemText =
    delta && delta.problem_definition != null && delta.problem_definition !== prevProblem
      ? oneLine(String(delta.problem_definition))
      : "";
  const firstProblem = !!problemText && prevProblem == null;
  if (firstProblem) candidates.push(["Problem", problemText]);
  if (delta) {
    const resolution = oneLine(delta.resolution);
    const rootCause = oneLine(delta.root_cause);
    if (resolution) candidates.push(["Resolved", resolution]);
    if (rootCause) candidates.push(["Root cause", rootCause]);
    const ruled = ruledOutOf(delta);
    ruled.filter((r) => !isBlank(r.why))
      .forEach((r) => candidates.push(["Ruled out", oneLine(String(r.cause))]));
    asList(delta.causes_activated).forEach((c) => candidates.push(["New cause", oneLine(c)]));
    asList(delta.facts_added).forEach((f) => candidates.push(["Fact", oneLine(f)]));
    asList(delta.symptoms_added).forEach((s) => candidates.push(["Symptom", oneLine(s)]));
    if (problemText && !firstProblem) candidates.push(["Problem", problemText]);
    // Never a title, always "one more": a cause nobody tried and the note.
    ruled.filter((r) => isBlank(r.why))
      .forEach((r) => candidates.push(["Not tested", oneLine(String(r.cause))]));
    const note = oneLine(delta.note);
    if (note) candidates.push(["Note", note]);
  }

  const TITLE_PREFIXES = ["Not tested", "Note"];
  const headIdx = candidates.findIndex(([p]) => !TITLE_PREFIXES.includes(p));
  const describe = ([p, t]) => `${p}: ${t}`;

  if (headIdx >= 0) {
    const [prefix, text] = candidates[headIdx];
    const others = candidates.filter((_, i) => i !== headIdx).map(describe);
    return { prefix, text, more: others.length, others };
  }

  // No headline-worthy change: say what the engineer did.
  const others = candidates.map(describe);
  let act = action && action.text ? action : null;
  if (!act) {
    const q = oneLine(hybridTurn?.question_text);
    if (q) act = { kind: "asked", text: q };
  }
  if (!act) {
    const typed = (items || [])
      .map(({ step }) => step)
      .find((s) => s && s.type === "digression" && oneLine(s.query_text));
    if (typed) act = { kind: "asked", text: oneLine(typed.query_text) };
  }
  if (act) {
    return {
      prefix: act.kind === "answered" ? "Answered" : "Asked",
      text: `"${act.text}"`,
      more: others.length,
      others,
    };
  }
  const lastStep = (items || []).map(({ step }) => step).filter(Boolean).pop();
  const stepText = lastStep ? oneLine(stepHeadline(lastStep)) : "";
  if (stepText) {
    return { prefix: stepLabel(lastStep), text: stepText, more: others.length, others };
  }
  return { prefix: "", text: `Turn ${turnNo}`, more: others.length, others };
}

/**
 * Past this many steps a turn's guided body starts folded. A real walk puts
 * all sixteen of its steps on ONE turn — printed open, that single card is
 * longer than the rest of the conversation put together and the timeline
 * stops being a timeline. Three or fewer is not a wall, so it stays open.
 */
const COLLAPSE_STEPS_OVER = 3;

/* ── tiny read-only visuals matching CaseRecord.jsx ────────────────── */

/** Active cause — green check, same vocabulary as CaseRecord's CAUSE_STATES. */
function ActiveBadge() {
  return (
    <span
      className="tb-cause-badge tb-cause-badge--active"
      title="Still in play"
    >
      <Check size={12} />
    </span>
  );
}

/** Ruled-out cause — rose X, same vocabulary as CaseRecord's CAUSE_STATES. */
function RuledOutBadge() {
  return (
    <span
      className="tb-cause-badge tb-cause-badge--ruled-out"
      title="Ruled out"
    >
      <X size={12} />
    </span>
  );
}

/* ── the case-record deltas for one turn ───────────────────────────── */

/**
 * What the case record LEARNED at this turn. Sections with no content render
 * nothing; `problem_definition` shows only when it differs from the previous
 * delta-carrying turn.
 *
 * Returns null when the turn changed nothing — the card decides whether that
 * silence needs a line of its own (it does only when the turn has no body
 * either).
 */
export function TurnDeltas({ turn, prevProblem }) {
  const symptoms = asList(turn.symptoms_added);
  const facts = asList(turn.facts_added);
  const activated = asList(turn.causes_activated);
  const ruledOut = Array.isArray(turn.causes_ruled_out)
    ? turn.causes_ruled_out.filter((r) => r && typeof r === "object" && r.cause)
    : [];

  const problemChanged =
    turn.problem_definition != null &&
    turn.problem_definition !== prevProblem;

  const hasContent =
    symptoms.length > 0 ||
    facts.length > 0 ||
    activated.length > 0 ||
    ruledOut.length > 0 ||
    problemChanged ||
    turn.root_cause ||
    turn.resolution ||
    turn.note;

  if (!hasContent) return null;

  return (
    <div className="tb-deltas">
      {/* Problem definition — only when it changed from the previous turn */}
      {problemChanged && (
        <div className="tb-section">
          <p className="tb-section-label">Problem refined</p>
          <p className="tb-text">{turn.problem_definition}</p>
        </div>
      )}

      {/* Symptoms added */}
      {symptoms.length > 0 && (
        <div className="tb-section">
          <p className="tb-section-label">Symptoms learned</p>
          <ul className="tb-list">
            {symptoms.map((s, i) => (
              <li key={i} className="tb-list-item">
                <span className="tb-dot tb-dot--symptom" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Facts added */}
      {facts.length > 0 && (
        <div className="tb-section">
          <p className="tb-section-label">Facts learned</p>
          <ul className="tb-list">
            {facts.map((f, i) => (
              <li key={i} className="tb-list-item">
                <span className="tb-dot tb-dot--fact" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Causes activated */}
      {activated.length > 0 && (
        <div className="tb-section">
          <p className="tb-section-label">Causes activated</p>
          <ul className="tb-list">
            {activated.map((c, i) => (
              <li key={i} className="tb-list-item">
                <ActiveBadge />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Causes ruled out — blank why → "not tested" */}
      {ruledOut.length > 0 && (
        <div className="tb-section">
          <p className="tb-section-label">Causes evaluated</p>
          <ul className="tb-list">
            {ruledOut.map((r, i) => {
              const notTested = isBlank(r.why);
              return (
                <li key={i} className="tb-list-item">
                  {notTested ? <ActiveBadge /> : <RuledOutBadge />}
                  <span>
                    {r.cause}
                    {notTested ? (
                      <span className="tb-not-tested"> — not tested</span>
                    ) : (
                      <span className="tb-why"> — {r.why}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Root cause identified */}
      {turn.root_cause && (
        <div className="tb-section">
          <p className="tb-section-label">Root cause</p>
          <p className="tb-text tb-root-cause">
            <Crown size={12} className="tb-crown" />
            {turn.root_cause}
          </p>
        </div>
      )}

      {/* Resolution */}
      {turn.resolution && (
        <div className="tb-section">
          <p className="tb-section-label">Resolution</p>
          <p className="tb-text">{turn.resolution}</p>
        </div>
      )}

      {/* Note */}
      {turn.note && (
        <div className="tb-section">
          <p className="tb-section-label">Note</p>
          <p className="tb-text tb-note">{turn.note}</p>
        </div>
      )}
    </div>
  );
}

/* ── the guided body ───────────────────────────────────────────────── */

/**
 * One turn's step cards, folded behind a summary line when there are enough
 * of them to bury the rest of the timeline.
 *
 * The summary says what is still OWED — "16 steps · 13 need a mark" — so the
 * submit gate stays readable without expanding anything, and the collapsed
 * header carries `data-steps-unmarked` so the footer's "N steps still need a
 * mark" can scroll to it even while the cards themselves are not mounted.
 */
function GuidedBody({
  items,
  total,
  results,
  notes,
  readOnly,
  canEdit,
  confirmedMarks,
  onMark,
  onNote,
  onStepClick,
  onMoveStep,
  onRemoveStep,
  foldable = true,
}) {
  /* The review variant shows every step open: there, reviewing the cards IS
     the screen, and a fold is one more click per turn. */
  const collapsible = foldable && items.length > COLLAPSE_STEPS_OVER;
  const [open, setOpen] = useState(!collapsible);
  const unmarked = items.filter(
    ({ step }) => isMarkable(step) && !results[step.step_no]
  ).length;
  const stepsLabel = `${items.length} step${items.length > 1 ? "s" : ""}`;

  const list = (
    <StepItems
      items={items}
      total={total}
      results={results}
      notes={notes}
      readOnly={readOnly}
      canEdit={canEdit}
      confirmedMarks={confirmedMarks}
      onMark={onMark}
      onNote={onNote}
      onStepClick={onStepClick}
      onMoveStep={onMoveStep}
      onRemoveStep={onRemoveStep}
    />
  );

  if (!collapsible) {
    return (
      <div className="tb-body">
        <p className="tb-body-label">
          {stepsLabel}
          {unmarked > 0 && <span className="tb-owed"> · {unmarked} need a mark</span>}
        </p>
        {list}
      </div>
    );
  }

  return (
    <div className="tb-body">
      <button
        type="button"
        className="tb-fold"
        aria-expanded={open}
        data-steps-unmarked={!open && unmarked > 0 ? "true" : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <ChevronRight size={12} className={open ? "tb-fold-open" : ""} />
        <span>
          {stepsLabel}
          {unmarked > 0 && <span className="tb-owed"> · {unmarked} need a mark</span>}
        </span>
        <span className="tb-fold-hint">{open ? "hide" : "show"}</span>
      </button>
      {open && list}
    </div>
  );
}

/* ── merge ─────────────────────────────────────────────────────────── */

/**
 * Build the timeline rows from the three sources.
 *
 * Turn numbers are NOT renumbered and gaps are NOT filled: a session whose
 * extractor spoke at turns 1, 3, 5, 6 while the walk lives on turn 2 renders
 * exactly 1, 2, 3, 5, 6. A turn appears because SOME source mentions it.
 *
 * Returns { rows, untied } where a row is
 *   { turnNo, delta, prevProblem, items, hybridTurn }
 * and `untied` is the steps with no usable turn_no (never dropped).
 */
export function buildTurnRows(turnState, steps, hybrid) {
  const deltas = (Array.isArray(turnState) ? turnState : [])
    .filter((t) => t && typeof t === "object")
    .slice()
    .sort((a, b) => (a.turn_no ?? 0) - (b.turn_no ?? 0));

  const groups = groupStepsByTurn(Array.isArray(steps) ? steps : []);
  const untied = groups.find((g) => g.turnNo == null)?.items || [];
  const stepsByTurn = new Map(
    groups.filter((g) => g.turnNo != null).map((g) => [g.turnNo, g.items])
  );

  const hybridByTurn = new Map(
    (Array.isArray(hybrid) ? hybrid : [])
      .filter((h) => h && typeof h === "object")
      .map((h) => [Number(h.turn_no), h])
  );

  /* "Problem refined" is a diff against the previous turn that SAID something
     about the problem — the sorted deltas, not the merged rows, so inserting
     a guided turn between two deltas cannot change what the card claims. */
  // The last NON-NULL problem before each delta: a delta that says nothing
  // about the problem (an observations-only turn) must not reset the diff, or
  // the next turn re-announces the same problem as if it were new.
  const prevProblemFor = new Map();
  let lastProblem = null;
  deltas.forEach((t) => {
    prevProblemFor.set(t, lastProblem);
    if (t.problem_definition != null) lastProblem = t.problem_definition;
  });

  const turnNos = new Set();
  deltas.forEach((t) => {
    const n = Number(t.turn_no);
    if (Number.isFinite(n)) turnNos.add(n);
  });
  stepsByTurn.forEach((_, n) => turnNos.add(n));
  hybridByTurn.forEach((_, n) => {
    if (Number.isFinite(n)) turnNos.add(n);
  });

  const deltaByTurn = new Map(
    deltas
      .filter((t) => Number.isFinite(Number(t.turn_no)))
      .map((t) => [Number(t.turn_no), t])
  );

  const rows = [...turnNos]
    .sort((a, b) => a - b)
    .map((turnNo) => {
      const delta = deltaByTurn.get(turnNo) || null;
      return {
        turnNo,
        delta,
        prevProblem: delta ? prevProblemFor.get(delta) ?? null : null,
        items: stepsByTurn.get(turnNo) || [],
        hybridTurn: hybridByTurn.get(turnNo) || null,
      };
    });

  return { rows, untied };
}

/* ── one row ───────────────────────────────────────────────────────── */

/**
 * A turn is a row of the learnings list's shape: chevron, numbered circle
 * (the turn number, not the row index — gaps are real), the summary in the
 * title slot, and a right-aligned kind chip where the turn concluded the case.
 */

function TurnCard({
  row,
  active,
  expanded,
  onToggle,
  showDeltas,
  showBodies,
  total,
  results,
  notes,
  readOnly,
  canEdit,
  confirmedMarks,
  confirmedHybrid,
  hybridMarks,
  hybridReasons,
  showStepBodies,
  onMarkStep,
  onNoteStep,
  onMarkHybrid,
  onReasonHybrid,
  onTurnClick,
  onMoveStep,
  onRemoveStep,
  cardRef,
  collapsible = true,
}) {
  const { turnNo, items, hybridTurn } = row;
  /* A view that does not show deltas does not summarise or badge them either:
     the row is there to keep the turn set, not to repeat the other tab. */
  const delta = showDeltas ? row.delta : null;
  const guided = items.length > 0;
  const fast = !!hybridTurn;
  const isConclusion = !!(delta && (delta.root_cause || delta.resolution));

  /* The title is the row's headline, open or shut. The DIAGNOSTIC / HYBRID
     chips carry "this turn had steps / Q&A", so the title never says it. */
  const title = useMemo(
    () => titleForTurn({
      turnNo,
      delta,
      prevProblem: row.prevProblem,
      action: row.action,
      hybridTurn,
      items,
    }),
    [turnNo, delta, row.prevProblem, row.action, hybridTurn, items]
  );
  const fullTitle = title.prefix ? `${title.prefix}: ${title.text}` : title.text;
  /* The title already reads `Asked: "<question>"`: the Q&A card's ASKED block
     would repeat it verbatim as the first line under it. */
  const titleIsQuestion =
    title.prefix === "Asked" &&
    !!hybridTurn?.question_text &&
    title.text === `"${oneLine(hybridTurn.question_text)}"`;

  const deltas = delta ? (
    <TurnDeltas turn={delta} prevProblem={row.prevProblem} />
  ) : null;
  /* The header row serves two roles depending on props:
     1. Collapse toggle (always, via onToggle)
     2. Transcript scroll sync (onTurnClick, when present in the Correction tab)
     Both fire on the same click; the toggle is the primary action. */
  const handleHeaderClick = useCallback(() => {
    if (collapsible) onToggle();
    if (onTurnClick) onTurnClick(turnNo);
  }, [collapsible, onToggle, onTurnClick, turnNo]);
  /* Not collapsible (the review variant): always open, and the header is a
     button only when it has something to do — the transcript scroll sync. */
  const open = collapsible ? expanded : true;
  const headerIsButton = collapsible || !!onTurnClick;

  const handleHeaderKey = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleHeaderClick();
      }
    },
    [handleHeaderClick]
  );

  return (
    <div
      ref={cardRef}
      className={[
        collapsible ? "info-entry" : "tb-review-turn",
        "tb-row",
        isConclusion ? "tb-row--conclusion" : "",
        active ? "tb-row--active" : "",
      ].filter(Boolean).join(" ")}
      data-turn={turnNo}
    >
      <div
        className={collapsible
          ? "info-entry-collapsed"
          : `info-entry-collapsed tb-review-head${headerIsButton ? "" : " tb-row-static"}`}
        role={headerIsButton ? "button" : undefined}
        tabIndex={headerIsButton ? 0 : undefined}
        aria-expanded={collapsible ? expanded : undefined}
        aria-label={`Turn ${turnNo}: ${fullTitle}${title.more ? ` (+${title.more} more)` : ""}`}
        onClick={headerIsButton ? handleHeaderClick : undefined}
        onKeyDown={headerIsButton ? handleHeaderKey : undefined}
      >
        {collapsible && (
          <ChevronRight
            size={12}
            className={`info-entry-chevron${expanded ? " info-entry-chevron--open" : ""}`}
          />
        )}
        <span className="info-entry-num" title={`Turn ${turnNo}`}>{turnNo}</span>
        {/* The wrapper takes the row's free width; inside it the title shrinks
            to its text so "+N more" sits right after it, not at the far end. */}
        <span className="tb-title-wrap">
          <span className="info-entry-title" title={fullTitle}>
            {title.prefix && <span className="tb-title-prefix">{title.prefix}:</span>}
            {title.prefix ? " " : ""}
            {title.text}
          </span>
          {title.more > 0 && (
            <span className="tb-title-more" title={title.others.join("\n")}>
              +{title.more} more
            </span>
          )}
        </span>
        {/* Derived from what the turn HAS, never from the trail's session-level
            `modes` — that says "fast + guided" for every turn of a mixed
            session, including the ones that were neither. */}
        {guided && (
          <span className="tb-mode-badge tb-mode-badge--guided" title="Diagnostic walk">
            DIAGNOSTIC
          </span>
        )}
        {fast && (
          <span className="tb-mode-badge tb-mode-badge--fast" title="Question and answer">
            HYBRID
          </span>
        )}
        {isConclusion && <span className="info-entry-kind tb-kind--conclusion">Conclusion</span>}
      </div>

      {open && (
        <div className={collapsible ? "info-entry-body" : "tb-review-body"}>
          {deltas}

          {guided && showBodies && showStepBodies && (
            <GuidedBody
              items={items}
              total={total}
              results={results}
              notes={notes}
              readOnly={readOnly}
              canEdit={canEdit}
              confirmedMarks={confirmedMarks}
              onMark={onMarkStep}
              onNote={onNoteStep}
              onStepClick={onTurnClick}
              onMoveStep={onMoveStep}
              onRemoveStep={onRemoveStep}
              foldable={collapsible}
            />
          )}
          {/* In graph view the steps are drawn as the whole walk above, so the card
              says how many belong to this turn rather than repeating them. */}
          {guided && showBodies && !showStepBodies && (
            <p className="tb-body-label">
              {items.length} step{items.length > 1 ? "s" : ""} in the trace above
            </p>
          )}

          {fast && showBodies && (
            <div className="tb-body">
              <HybridTurn
                turn={hybridTurn}
                mark={hybridMarks[hybridTurn.turn_no]}
                reason={hybridReasons[hybridTurn.turn_no]}
                readOnly={readOnly}
                onMark={onMarkHybrid}
                onReason={onReasonHybrid}
                showHeader={false}
                showQuestion={!titleIsQuestion}
                confirmed={!!confirmedHybrid?.[hybridTurn.turn_no]}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── the component ─────────────────────────────────────────────────── */

/**
 * Props:
 *   turnState     — the `turn_state` array (may be absent, null, or empty)
 *   steps         — the trail's steps (guided walk)
 *   results/notes — step marks and their "why", { [step_no]: … }
 *   hybrid        — the fast Q&A rows
 *   hybridMarks / hybridReasons — hybrid marks and their "why", { [turn_no]: … }
 *   heading       — the <h2> above the timeline. Pass null to drop it: inside
 *                   the overlay's Correction tab the tab label IS the heading,
 *                   and a second one directly under it reads as a bug.
 *   toolbar       — node at the right of the heading row (the List/Graph
 *                   toggle)
 *   actions       — more right-hand controls on the heading row, after the
 *                   toolbar (the Expert tab's "Confirm all N AI suggestions")
 *   graph         — node rendered ABOVE the heading row instead of the per-turn
 *                   step bodies. The graph is a whole-walk view and does not
 *                   decompose per turn, so it replaces the step cards rather
 *                   than living inside one.
 *   activeTurn / onTurnClick — the two-way scroll sync with the transcript
 *   showDeltas    — render the case-record deltas (default true)
 *   showBodies    — render the step cards and the Q&A (default true)
 *
 * ── THE TURN SET IS THE SAME IN EVERY VIEW ──
 * A turn is a row when ANY of the three sources mentions it — a `turn_state`
 * delta, a walk step or a hybrid Q&A row — whatever the view shows inside it.
 * The overlay mounts this twice: the Case learning tab shows only the deltas
 * (`showBodies={false}`), the Expert tab only the steps and Q&A
 * (`showDeltas={false}`). Both mounts pass all three sources.
 *
 * ── …BUT EACH VIEW RENDERS ONLY ITS OWN ROWS ──
 * A row with nothing of the view's own kind (Case Record: no delta content;
 * Expert: no steps and no Q&A) is NOT rendered — it used to be, as a one-line
 * "No case record changes this turn." apology. The heading counts rendered
 * rows, so the two tabs' "N turns" can differ: each counts what it shows.
 * See `rowIsVisible`.
 *
 *   turns         — the trail payload's stored turn envelopes (`data.turns`),
 *                   used ONLY for the row title's "Answered:" / "Asked:"
 *                   fallback. Optional.
 */
export default function TurnBreakdown({
  turnState,
  steps = [],
  turns = null,
  results = {},
  notes = {},
  hybrid = [],
  hybridMarks = {},
  hybridReasons = {},
  confirmedMarks = {},
  confirmedHybrid = {},
  readOnly = false,
  canEdit = false,
  activeTurn = null,
  heading = "Turn breakdown",
  toolbar = null,
  actions = null,
  graph = null,
  onMarkStep,
  onNoteStep,
  onMarkHybrid,
  onReasonHybrid,
  onTurnClick,
  onMoveStep,
  onRemoveStep,
  showDeltas = true,
  showBodies = true,
  variant = "rows",
}) {
  /* "review" (the overlay's Expert tab only): every turn open as a section —
     header band then its step cards / Q&A — with no chevrons, no Expand all,
     no outer card; and when a `graph` is passed the graph IS the view, so the
     list is not rendered under it. "rows" (default): the collapsible
     rows-in-a-card list the Case Record tab and TrailReview use. */
  const review = variant === "review";
  const { rows: allRows, untied } = useMemo(
    () => buildTurnRows(turnState, steps, hybrid),
    [turnState, steps, hybrid]
  );
  const actionByTurn = useMemo(() => engineerActions(turns), [turns]);
  /* Only the rows this view has something of its own for; the heading count,
     Expand all and the collapse-state key all read THIS list. */
  const rows = useMemo(
    () =>
      allRows
        .filter((r) => rowIsVisible(r, { showDeltas, showBodies }))
        .map((r) => ({ ...r, action: actionByTurn.get(r.turnNo) || null })),
    [allRows, actionByTurn, showDeltas, showBodies]
  );

  const cardRefs = useRef({});
  const prevActiveRef = useRef(null);

  /* ── per-card collapse state ──
     Default: all collapsed. Keyed by turnNo.

     When the set of turn numbers changes (data arrives asynchronously, a
     background refresh adds or removes turns), the map resets so every card
     starts collapsed again. Without this, cards that appear between renders
     can be visually out of sync with the map — the CSS class and the JSX
     body gate both derive from the map, but the card's default styling
     (an open body) reads as "expanded" to the eye. */
  const [expandedMap, setExpandedMap] = useState({});
  const prevTurnKey = useRef("");

  // A cheap fingerprint of which turns the timeline currently has.
  const turnKey = useMemo(
    () => rows.map((r) => r.turnNo).join(","),
    [rows]
  );

  // Reset when the turn set changes — NOT on every render, only when the
  // actual set of turn numbers differs.
  if (turnKey !== prevTurnKey.current) {
    prevTurnKey.current = turnKey;
    // Only reset if the map is non-empty: the initial `{}` is already correct,
    // and resetting it would schedule a useless state update.
    if (Object.keys(expandedMap).length > 0) {
      setExpandedMap({});
    }
  }

  const toggleCard = useCallback((turnNo) => {
    setExpandedMap((prev) => ({ ...prev, [turnNo]: !prev[turnNo] }));
  }, []);

  /* Derived from the SAME expression each card uses (`!!expandedMap[turnNo]`).
     Kept inline so it cannot drift from the card's `expanded` prop. */
  const isExpanded = useCallback(
    (turnNo) => !!expandedMap[turnNo],
    [expandedMap]
  );
  const anyCollapsed = rows.some((r) => !isExpanded(r.turnNo));

  const toggleAll = useCallback(() => {
    if (anyCollapsed) {
      // Expand all
      const next = {};
      rows.forEach((r) => { next[r.turnNo] = true; });
      setExpandedMap(next);
    } else {
      // Collapse all
      setExpandedMap({});
    }
  }, [anyCollapsed, rows]);

  /* Bring the active turn into view when the selection changes.
     `block: "nearest"` means an already-visible card does not move, so a
     click on a visible turn never yanks the pane. Nothing observes the left
     pane's scroll, so this cannot feed back into the transcript. */
  useEffect(() => {
    if (activeTurn == null) return;
    if (activeTurn === prevActiveRef.current) return;
    prevActiveRef.current = activeTurn;
    const el = cardRefs.current[activeTurn];
    if (!el || typeof el.scrollIntoView !== "function") return;
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeTurn]);

  /* Absent/null turn_state is "not extracted yet", which is different from an
     empty array — but only worth SAYING when there is nothing else to show.
     A trail with steps or hybrid rows renders its timeline either way: the
     deltas are one of three sources, never the gate on the other two. */
  if (rows.length === 0 && untied.length === 0) {
    return (
      <section className="tb-root">
        {heading && <h2 className="tb-heading">{heading}</h2>}
        <p className="tb-empty">
          {turnState == null
            ? "Turn breakdown has not been extracted for this trail yet."
            : "No turn data recorded for this trail."}
        </p>
      </section>
    );
  }

  const totalSteps = Array.isArray(steps) ? steps.length : 0;
  const shared = {
    total: totalSteps,
    results,
    notes,
    readOnly,
    canEdit,
    confirmedMarks,
    confirmedHybrid,
    hybridMarks,
    hybridReasons,
    showStepBodies: !graph,
    showDeltas,
    showBodies,
    collapsible: !review,
    onMarkStep,
    onNoteStep,
    onMarkHybrid,
    onReasonHybrid,
    onTurnClick,
    onMoveStep,
    onRemoveStep,
  };

  /* ORDER: ONE heading row — title and count on the left; view toggle, the
     caller's actions and Expand all on the right — then the whole-walk graph,
     then the row card. The toggle used to have a row of its own above the
     graph (and the Expert tab's "Confirm all" a third row above that), which
     stacked three right-aligned controls over an empty band. Expand all
     shows only when the per-turn rows are the view (no graph): in graph view
     the graph is what the reader is looking at. */
  const showExpandAll = rows.length > 1 && !graph && !review;
  const showList = !(review && graph);
  const hasControls = !!(toolbar || actions || showExpandAll);
  return (
    <section className="tb-root">
      {(heading || hasControls) && (
        <div className="tb-head">
          {heading && (
            <h2 className="tb-heading">
              {heading}
              {rows.length > 0 && (
                <span className="tb-heading-count">
                  {rows.length} turn{rows.length > 1 ? "s" : ""}
                </span>
              )}
            </h2>
          )}
          {hasControls && (
            <div className="tb-head-controls">
              {toolbar}
              {actions}
              {showExpandAll && (
                <button
                  type="button"
                  className="tb-expand-all"
                  onClick={toggleAll}
                >
                  {anyCollapsed ? "Expand all" : "Collapse all"}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {graph && <div className="tb-graph">{graph}</div>}

      {/* One white card, rows separated by hairlines — the learnings list's
          shape (`.info-list-card` is shared with LearningEntries). The count
          lives in the heading above, so the card holds rows only. */}
      {showList && (rows.length > 0 || (untied.length > 0 && !graph && showBodies)) && (
        <div className={review ? "tb-review-list" : "info-list-card"}>
          {rows.map((row) => (
            <TurnCard
              key={row.turnNo}
              row={row}
              active={activeTurn != null && activeTurn === row.turnNo}
              expanded={isExpanded(row.turnNo)}
              onToggle={() => toggleCard(row.turnNo)}
              cardRef={(el) => {
                cardRefs.current[row.turnNo] = el;
              }}
              {...shared}
            />
          ))}

          {/* Steps the capture could not tie to a turn. They are part of the
              walk and must stay reachable, so they get a row of their own at
              the end rather than being folded into the last real turn. In graph
              view they are already drawn in the trace, so the row has nothing
              left to say. Always open: there is no turn to summarise. */}
          {untied.length > 0 && !graph && showBodies && (
            <div className={`${review ? "tb-review-turn" : "info-entry"} tb-row tb-row--untied`}>
              <div className={`info-entry-collapsed tb-row-static${review ? " tb-review-head" : ""}`}>
                <span className="info-entry-title">Not tied to a turn</span>
                <span className="tb-mode-badge tb-mode-badge--guided">DIAGNOSTIC</span>
              </div>
              <div className={review ? "tb-review-body" : "info-entry-body"}>
                <GuidedBody
                  items={untied}
                  foldable={!review}
                  total={totalSteps}
                  results={results}
                  notes={notes}
                  readOnly={readOnly}
                  canEdit={canEdit}
                  confirmedMarks={confirmedMarks}
                  onMark={onMarkStep}
                  onNote={onNoteStep}
                  onStepClick={onTurnClick}
                  onMoveStep={onMoveStep}
                  onRemoveStep={onRemoveStep}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
